import { computed, effect, inject, Injectable, signal, type Signal } from '@angular/core';
import { avatarInitials } from '@aurora/lib';
import { CurrentAccountService, TokenStore } from '@aurora/modules/authentication';
import { LangService } from '@aurora/modules/lang';
import { TranslocoService } from '@jsverse/transloco';
import { HlmDialogService } from '@spartan-ng/helm/dialog';
import { type IamAccount } from '@src/src/app/aurora.types';
import { Apollo } from 'apollo-angular';
import { catchError, of, Subject, switchMap } from 'rxjs';
import { ACCOUNT_PROFILE_DESCRIPTION_ID } from '../data-access/account-profile.derive';
import {
  addMeMetaItemsMutation,
  meAccountQuery,
  removeMeMetaItemsMutation,
} from '../data-access/account.graphql';

/**
 * IAM adapter for the `CurrentAccountService` port: loads the authenticated account via the IAM
 * `iamMeAccount` query and exposes it as display-ready signals. Wired in `aurora.provider.ts`.
 * Best-effort: a failed load leaves the signals blank so the shell renders a neutral placeholder.
 *
 * ## Permission surface
 *
 * On a resolved `iamMeAccount`, `dPermissions.all` (a `string[]` inside the `JSON` scalar) is read
 * into `permissions` and `permissionsLoaded` flips to `true`. On a failed query, `permissions`
 * stays empty but `permissionsLoaded` still flips `true` — best-effort, never stalls consumers.
 *
 * `dPermissions` is typed as `Scalars['JSON']['output']` (i.e. `any`). We narrow `dPermissions.all`
 * to `string[]` with a local type guard rather than a double cast, per HR-ACL-001.
 */
@Injectable()
export class CurrentAccountIamAdapter extends CurrentAccountService {
  private readonly apollo = inject(Apollo);
  private readonly tokenStore = inject(TokenStore);
  private readonly dialog = inject(HlmDialogService);
  private readonly transloco = inject(TranslocoService);
  private readonly langService = inject(LangService);
  private readonly account = signal<IamAccount | null>(null);
  private readonly avatarSig = signal<string | null>(null);
  private readonly permissionsSig = signal<readonly string[]>([]);
  private readonly permissionsLoadedSig = signal(false);
  private readonly loadedSig = signal(false);
  /** The profile `langId` the last successful load carried; `undefined` before the first one. */
  private lastProfileLangId: string | null | undefined;
  /** A profile language waiting to become the active one, set only when `langId` changed. */
  private readonly pendingLangId = signal<string | null>(null);
  /**
   * Fires one `load()` request through a `switchMap`: two `load()` calls can overlap in
   * production (the initializer's edge-triggered load races the landing
   * resolver's own re-armed load on first sign-in; an impersonate() shortly after login is
   * another window). Subscribing per call independently let whichever response arrived LAST win,
   * even an OLDER (now-stale) one. `switchMap` cancels the older in-flight request the moment a
   * newer `load()` fires, so only the LATEST request's response is ever applied.
   */
  private readonly loadRequested = new Subject<void>();

  readonly displayName = computed(() => {
    const account = this.account();
    if (!account) return '';
    const name = account.user?.name;
    if (name) return account.user?.surname ? `${name} ${account.user.surname}` : name;
    return account.username ?? '';
  });

  readonly email = computed(() => this.account()?.email ?? '');
  readonly accountId = computed(() => this.account()?.id ?? null);
  readonly avatarSrc = this.avatarSig.asReadonly();
  readonly initials = computed(() => {
    const account = this.account();
    if (!account) return '';
    return avatarInitials(account.user?.name, account.user?.surname, account.username ?? '');
  });
  readonly permissions = this.permissionsSig.asReadonly();
  readonly permissionsLoaded = this.permissionsLoadedSig.asReadonly();
  /** First role carrying a non-null value, in the server's role order. Raw — see port JSDoc. */
  readonly defaultRedirection = computed(() => firstRoleRedirection(this.account()?.roles ?? null));
  /** OR across every role's flag, `null` treated as `false`. */
  readonly hasHiddenVerticalNavigation = computed(() =>
    anyRoleHidesNavigation(this.account()?.roles ?? null),
  );
  readonly loaded = this.loadedSig.asReadonly();

  constructor() {
    super();
    // One long-lived subscription for the adapter's lifetime (a root-level singleton, same shape
    // as AuthenticatedInitializerRunner's effect / SessionRecoveryCoordinatorService's flights —
    // none of them tear down either). switchMap cancels the PREVIOUS in-flight query's
    // subscription the instant a new `loadRequested` fires, so an older, slower response can never
    // overwrite a newer one that already settled.
    this.loadRequested
      .pipe(
        switchMap(() =>
          this.apollo
            .query<{ iamMeAccount: IamAccount; iamMeAvatarAccount?: string | null }>({
              query: meAccountQuery,
            })
            .pipe(catchError(() => of(null))),
        ),
      )
      .subscribe((result) => {
        const account = result?.data?.iamMeAccount ?? null;
        this.account.set(account);
        this.avatarSig.set(result?.data?.iamMeAvatarAccount ?? null);
        this.permissionsSig.set(extractPermissions(account?.dPermissions));
        this.permissionsLoadedSig.set(true);
        this.loadedSig.set(true);
        if (account) this.preferProfileLang(account.user?.langId ?? null);
      });

    // Sign-out only clears the token store; without this the previous account would linger and
    // consumers keyed on `accountId()` (the manual index) would never see the session end.
    effect(() => {
      if (this.tokenStore.authenticated()) return;
      this.account.set(null);
      this.avatarSig.set(null);
      this.permissionsSig.set([]);
    });

    // Resolves the pending profile language against the panel langs, which may arrive after the
    // account: the effect re-runs when they do. Unknown ids stay pending and change nothing.
    effect(() => {
      const langId = this.pendingLangId();
      if (!langId) return;
      const lang = this.langService.panel().find((panelLang) => panelLang.id === langId);
      if (!lang) return;
      this.transloco.setActiveLang(lang.iso6392);
      this.pendingLangId.set(null);
    });
  }

  hasPermission(permission: string): boolean {
    if (!this.permissionsLoadedSig()) return false;
    return this.permissionsSig().includes(permission);
  }

  metaList(key: string): Signal<readonly unknown[]> {
    return computed(() => extractMetaList(this.account()?.user?.meta, key));
  }

  addMetaItems(key: string, items: unknown): void {
    // No catchError: a refused write must reach the global GraphQL error handling, like the other
    // mutations of the account. The list is replaced only by the server's answer.
    this.apollo
      .mutate({ mutation: addMeMetaItemsMutation, variables: { payload: { key, items } } })
      .subscribe((result) => this.applyMetaList(key, result.data?.iamAddMeMetaItems));
  }

  removeMetaItems(key: string, items: unknown): void {
    this.apollo
      .mutate({ mutation: removeMeMetaItemsMutation, variables: { payload: { key, items } } })
      .subscribe((result) => this.applyMetaList(key, result.data?.iamRemoveMeMetaItems));
  }

  /** Sets `user.meta[key]` to the list the server stored, keeping every other `meta` key. */
  private applyMetaList(key: string, list: unknown): void {
    if (!Array.isArray(list)) return;
    this.account.update((account) => {
      if (!account?.user) return account;
      const meta: Record<string, unknown> = isRecord(account.user.meta) ? account.user.meta : {};
      return { ...account, user: { ...account.user, meta: { ...meta, [key]: list } } };
    });
  }

  load(): void {
    // Re-arm BEFORE issuing the query: an impersonation swap calls load() while a PREVIOUS
    // account is still loaded, and a consumer computing the landing route must see "not ready"
    // for the whole in-flight window, not the stale previous session's readiness.
    this.loadedSig.set(false);
    this.loadRequested.next();
  }

  /**
   * The profile language is a PREFERENCE: it becomes the active language on the first load and
   * whenever the saved `langId` changes, never on other reloads, so a temporary switch from the
   * header selector survives them.
   */
  private preferProfileLang(langId: string | null): void {
    const changed = langId !== this.lastProfileLangId;
    this.lastProfileLangId = langId;
    if (!changed) return;
    // `null` clears a still-pending language and applies nothing.
    this.pendingLangId.set(langId);
    if (!langId) return;
    // Panel langs hydrate from the session cache; a cold start needs the fetch.
    void this.langService.load().catch(() => undefined);
  }

  /**
   * Opens the profile dialog over the current screen. The dialog is imported lazily so it stays
   * out of the initial bundle (this adapter is wired eagerly in `aurora.provider.ts`); it gets the
   * session's account as its context to prefill the forms.
   */
  openProfile(): void {
    void import('../features/account-profile-dialog.component').then(({ AccountProfileDialog }) =>
      this.dialog.open(AccountProfileDialog, {
        contentClass: PROFILE_DIALOG_CLASS,
        ariaDescribedBy: ACCOUNT_PROFILE_DESCRIPTION_ID,
        context: { account: this.account() },
      }),
    );
  }
}

/**
 * Wider than the default dialog: the profile has a side navigation next to its form. The width is
 * explicit because the overlay pane shrinks to its content, so a max-width alone would let the
 * dialog resize when the user switches between the Account and Security sections.
 */
const PROFILE_DIALOG_CLASS = 'max-h-[90vh] w-[min(92vw,56rem)] overflow-y-auto sm:max-w-none';

/**
 * Narrows `roles` (typed by the generated `IamMeAccountQuery`, already non-`any`) to the two
 * UI-preference fields, tolerating the GraphQL-nullable array/element shape (`Maybe<...>[]`).
 */
type RoleUiPreferenceSource = ReadonlyArray<{
  defaultRedirection?: string | null;
  hasHiddenVerticalNavigation?: boolean | null;
} | null> | null;

/**
 * The first role (in server order) whose `defaultRedirection` is non-empty, or `null`. The
 * truthy check also rejects an empty string (`''`) — not just `null`/`undefined` — because an
 * empty string is not a path and must not be treated as "this role carries a redirection".
 */
function firstRoleRedirection(roles: RoleUiPreferenceSource): string | null {
  if (!roles) return null;
  for (const role of roles) {
    if (role?.defaultRedirection) return role.defaultRedirection;
  }
  return null;
}

/** `true` when ANY role sets `hasHiddenVerticalNavigation`; a `null`/missing flag reads `false`. */
function anyRoleHidesNavigation(roles: RoleUiPreferenceSource): boolean {
  if (!roles) return false;
  return roles.some((role) => role?.hasHiddenVerticalNavigation === true);
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

/** `meta[key]` when it is a list, else empty (`meta` is the free-form `JSON` scalar). */
function extractMetaList(meta: unknown, key: string): readonly unknown[] {
  if (!isRecord(meta)) return [];
  const list = meta[key];
  return Array.isArray(list) ? list : [];
}

/**
 * Narrows `dPermissions.all` from the GraphQL `JSON` scalar (`any`) to `string[]`.
 * Uses a runtime type guard (not a cast) per HR-ACL-001.
 *
 * `dPermissions` shape (from backend): `{ all: string[], roles: Record<string, string[]> }`.
 * We only need `all` — the flat list of all permissions the account holds.
 */
function extractPermissions(dPermissions: unknown): readonly string[] {
  if (dPermissions === null || dPermissions === undefined) return [];
  if (typeof dPermissions !== 'object') return [];

  const maybeAll = (dPermissions as Record<string, unknown>)['all'];
  if (!Array.isArray(maybeAll)) return [];

  return maybeAll.filter((item): item is string => typeof item === 'string');
}
